import { Bot, Settings, TelegramUser, TimeSlices, User, UserSettings } from "../_app/types";
import { _adjustInvestment } from "./_adjustInvestment";
import { _roundDec } from "./_roundToDecimals";
import formatNumber from "./formatNumber";
import { precision } from "./_precision";
import { jwtDecode } from "jwt-decode";

export function parseTelegramUserData(initData: string): TelegramUser {
  const params = new URLSearchParams(initData);

  let userObject = null;

  for (const [key, value] of params.entries()) {
    if (key === "user") {
      userObject = JSON.parse(decodeURIComponent(value));
      break;
    }
  }

  return userObject;
}

export function isTokenExpired(token: string): boolean {
  if (!token) return true;
  try {
    const { exp } = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    if (exp) {
      return exp < currentTime;
    } else {
      return true;
    }
  } catch (error: any) {
    console.error("Error decoding token", error);
    return true;
  }
}

export function convertRuToEng(input: string): string {
  const russianToEnglish: Record<string, string> = {
    й: "q",
    ц: "w",
    у: "e",
    к: "r",
    е: "t",
    н: "y",
    г: "u",
    ш: "i",
    щ: "o",
    з: "p",
    ф: "a",
    ы: "s",
    в: "d",
    а: "f",
    п: "g",
    р: "h",
    о: "j",
    л: "k",
    д: "l",
    я: "z",
    ч: "x",
    с: "c",
    м: "v",
    и: "b",
    т: "n",
    ь: "m",
    б: ",",
    ю: ".",
  };

  return input
    .toLowerCase()
    .split("")
    .map((char) => russianToEnglish[char] || char)
    .join("");
}

export function getTelegramNotificationValue(settings: UserSettings[]): boolean {
  let tgToggle = settings.find(
    (item: UserSettings) => item.name === "TELEGRAM_NOTIFICATION_ENABLED"
  );
  if (tgToggle) {
    if (tgToggle.value === "true") {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export function decodeUserInitData(initData: string): string {
  const userIndex = initData.indexOf("user=");

  if (userIndex !== -1) {
    const userData = initData.substring(userIndex);
    return decodeURIComponent(userData);
  } else {
    console.log("Параметр user не найден.");
  }
  return "";
}
export function decodeUserHash(initData: string): string {
  let decodedData = decodeURIComponent(initData);
  const userData = decodedData.split("&").find((param) => param.startsWith("hash="));
  if (userData) {
    const hash = userData.split("=")[1];
    return hash;
  } else {
    return "";
  }
}

export function printFloatValue(value: number): string {
  let str = String(value);
  return str.replace(".", ",");
}

export function printAPR(value: number): string {
  if (!value) return `${0}%`;
  return `${formatNumber(value * 100)}%`;
}

// export const calculateOrderAmount = (bot: Bot, stepInBase: number, stepInQuote: number, stepsCount: number) => {
//     if (stepsCount === Infinity) {
//         const adjustedInvestment = _adjustInvestment(bot.investment,
//             bot.currencyType === 'BASE' ? stepInBase : stepInQuote);
//         const steps = bot.currencyType === 'BASE'
//             ? adjustedInvestment / stepInBase
//             : adjustedInvestment / stepInQuote;
//
//         return _roundDec(steps, precision(bot.ticker.orderStep)) * bot.ticker.orderStep;
//     }
//     console.log(stepsCount);
//     return bot.ticker.orderStep * stepsCount;
// };

export const calculateStepsCount = (
  bot: Bot,
  stepInBase: number,
  stepInQuote: number,
  stepsCount: number
) => {
  if (stepsCount === Infinity) {
    const adjustedInvestment = _adjustInvestment(
      bot.investment,
      bot.currencyType === "BASE" ? stepInBase : stepInQuote
    );
    return bot.currencyType === "BASE"
      ? adjustedInvestment / stepInBase
      : adjustedInvestment / stepInQuote;
  }
  return stepsCount;
};

export function getMiddlePrice(min: number, max: number, precision: number): number {
  return _roundDec(Math.abs(max + min) / 2, precision);
}

export function formatCurrency(input: string, currency: string): string {
  const currencies = ["USDS", "USDC", "USD", "USDT"];
  const normalizedCurrency = currency.toUpperCase();

  if (currencies.includes(normalizedCurrency)) {
    return `$${input}`;
  } else {
    return `${input} ${currency}`;
  }
}

export function getMinInvestmentStepCount(tickerNotional: number, botNotional: number): number {
  if (tickerNotional === -1 || botNotional <= 0) return 1;

  let notionalRatio = tickerNotional / botNotional;

  return Math.max(1, Math.ceil(notionalRatio));
}

export function getBotNotional(botMinPrice: number, tickerOrderAmount: number): number {
  if (botMinPrice <= 0) return 0;
  if (tickerOrderAmount <= 0) return 0;

  return botMinPrice * tickerOrderAmount;
}

export function calcRoi(pnl: number, initialAmountQuote: number, leverage: number): number {
  if (pnl === null || pnl === 0 || initialAmountQuote === 0 || leverage === 0) {
    return 0;
  }
  // (initialAmountQuote / leverage) === MARGIN
  // pnl - totalPnL
  return pnl / (initialAmountQuote / leverage);
}

export function calcRoiByMargin(pnl: number, margin: number): number {
  if (pnl === null || pnl === 0 || margin === 0) {
    return 0;
  }
  // (initialAmountQuote / leverage) === MARGIN
  // pnl - totalPnL
  return pnl / margin;
}

export function printPriceStepRange(bot: Bot, min: number, max: number) {
  let result =
    bot.gridType === "ARITHMETIC"
      ? `${formatNumber(min, precision(bot.ticker.priceStep))}% \u2014 ${formatNumber(max, precision(bot.ticker.priceStep))}%`
      : `$${formatNumber(min, 4)} \u2014 $${formatNumber(max, 4)}`;
}

export function printRuntime(hours: number) {
  if (hours <= 0) return 0;

  const hoursInADay = 24;

  const days = Math.floor(hours / hoursInADay);
  const hoursNew = hours % hoursInADay;

  // Формируем строку для вывода
  if (days > 0) {
    return `${days}d ${hoursNew}h`;
  } else {
    return `${hoursNew}h`;
  }
}

export function calcApr(totalProfit: number, requiredMargin: number, runtimeHours: number): number {
  let finalRuntimeHours = runtimeHours === 0 ? 1 : runtimeHours;
  let ROI = totalProfit / requiredMargin;

  return (ROI / finalRuntimeHours) * 365 * 24;
}

export function calcTotalPnl(
  totalProfit: number,
  requiredMargin: number,
  runtimeDays: number
): number {
  let finalRuntimeDays = runtimeDays === 0 ? 1 : runtimeDays;

  return (totalProfit / requiredMargin / finalRuntimeDays) * 365;
}

export function calcRealizedProfit(realizedPnl: number, funding: number): number {
  return (realizedPnl ?? 0) + (funding ?? 0);
}

export function daysFromNow(inputDate: string): number {
  const targetDate = new Date(inputDate);
  const currentDate = new Date();

  currentDate.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  const differenceInMilliseconds = targetDate.getTime() - currentDate.getTime();

  const differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));

  return differenceInDays;
}

export function getDate(unix: number): string {
  let date = new Date(unix);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
}

export function getTime(unix: number): string {
  let date = new Date(unix);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return ` ${hours}:${minutes}`;
}

export function obtainTimeframeProfitPercent(interval: string): number {
  const profitPercentMap: Record<TimeSlices, number> = {
    [TimeSlices.ONE_MINUTE]: 0.0,
    [TimeSlices.FIVE_MINUTES]: 0.002,
    [TimeSlices.FIFTEEN_MINUTES]: 0.0035,
    [TimeSlices.THIRTY_MINUTES]: 0.005,
    [TimeSlices.ONE_HOUR]: 0.008,
    [TimeSlices.TWO_HOURS]: 0.015,
    [TimeSlices.FOUR_HOURS]: 0.025,
    [TimeSlices.SIX_HOURS]: 0.03,
    [TimeSlices.EIGHT_HOURS]: 0.035,
    [TimeSlices.TWELVE_HOURS]: 0.04,
    [TimeSlices.ONE_DAY]: 0.05,
  };

  const intervalEnum = interval as TimeSlices;
  return profitPercentMap[intervalEnum] ?? 0.0; // Возвращаем 0.0, если ключ не найден
}
